/* ── Corpus Browser Modal ─────────────────────────────────────
   Full-screen modal showing the full corpus inventory as an
   expandable tree: skill domains → individual standards.
   ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCorpus } from '../api';
import type { CorpusData, CorpusSkill } from '../types';

/* ── Static update feed data ─────────────────────────────────────
   NOTE: These hardcoded entries have been removed because the
   relative timestamps ("2 days ago" etc.) become stale immediately.
   Replace with a real /api/updates or /api/changelog endpoint.
   See: https://github.com/radmax/standards-app/issues/XXX
─────────────────────────────────────────────────────────────── */
const UPDATES: { title: string; description: string; time: string }[] = [];

/* Fix 13: Human-readable display names for skill slugs */
const SKILL_DISPLAY_NAMES: Record<string, string> = {
  'ncc-compliance': 'NCC / Building Code',
  'ncc-bca-compliance': 'NCC / BCA',
  'ncc-2025-bca': 'NCC 2025 (BCA)',
  'ncc-2022-bca': 'NCC 2022 (BCA)',
  'electrical-compliance': 'Electrical',
  'plumbing-installation': 'Plumbing',
  'fire-safety': 'Fire Safety',
  'watermark-scheme': 'WaterMark',
  'glazing-safety': 'Glazing',
  'eess-platform': 'EESS Equipment Safety',
  'product-safety-eess': 'Product Safety (EESS)',
  'dda-compliance': 'Disability Access (AS 1428)',
  'accessibility-compliance': 'Accessibility (AS 1428)',
  'access-disability': 'Accessibility',
  'energy-efficiency': 'Energy Efficiency',
  'structural-loads': 'Structural Loads (AS 1170)',
  'structural-adequacy': 'Structural',
  'nz-building-code': 'NZ Building Code',
  'uk-eu-compliance': 'UK / EU',
  'acl-consumer-law': 'Consumer Law (ACL)',
  'consumer-law': 'Consumer Law (ACL)',
  'consumer-protection': 'Consumer Protection',
  'appliance-safety': 'Appliance Safety (AS 60335)',
  'luminaire-safety': 'Luminaire Safety',
  'switchgear-safety': 'Switchgear Safety',
  'jas-anz-accreditation': 'JAS-ANZ Accreditation',
  'jasanz-certification': 'JAS-ANZ',
  'saa-approvals': 'SAA Approvals',
  'nata-accreditation': 'NATA',
};

/* ── Standard descriptions — shown in corpus browser expanded view ── */

const STANDARD_DESCRIPTIONS: Record<string, string> = {
  'AS/NZS 3000:2018': 'Wiring Rules — Requirements for the design, construction, and verification of electrical installations.',
  'AS/NZS 3000:2024': 'Wiring Rules (2024 Edition) — Updated requirements for electrical installations.',
  'AS/NZS 3001:2008': 'Transportable structures and vehicles — Electrical installations in non-fixed structures.',
  'AS/NZS 3008.1.1:2017': 'Selection of cables — Current-carrying capacity and voltage drop calculation.',
  'AS/NZS 3010:2017': 'High voltage electrical installations.',
  'AS/NZS 3012:2019': 'Construction and demolition sites — Electrical installations for temporary works.',
  'AS/NZS 3018:2001': 'Switchboard assemblies — Design and manufacture of low-voltage switchboards.',
  'AS/NZS 3019:2007': 'Periodic verification of electrical installations.',
  'AS/NZS 3760:2022': 'In-service safety inspection and testing of electrical equipment.',
  'AS/NZS 3820:1998': 'Essential safety requirements for electrical equipment.',
  'AS 3100:2022': 'General requirements for electrical equipment approval and testing.',
  'NCC 2025': 'National Construction Code 2025 — Building Code of Australia performance requirements.',
  'NCC 2022': 'National Construction Code 2022 — Building Code of Australia performance requirements.',
  'NCC 2025 Volume One': 'NCC 2025 Vol 1 — Commercial buildings (Class 2–9, 10b).',
  'NCC 2025 Volume Two': 'NCC 2025 Vol 2 — Residential buildings (Class 1 and 10).',
  'NCC 2022 Volume One': 'NCC 2022 Vol 1 — Commercial buildings.',
  'NCC 2022 Volume Two': 'NCC 2022 Vol 2 — Residential buildings.',
  'AS/NZS 3500.0:2021': 'Plumbing and drainage — Glossary of terms.',
  'AS/NZS 3500.1:2021': 'Plumbing and drainage Part 1 — Water services.',
  'AS/NZS 3500.2:2021': 'Plumbing and drainage Part 2 — Sanitary plumbing and drainage.',
  'AS/NZS 3500.3:2021': 'Plumbing and drainage Part 3 — Stormwater drainage.',
  'AS/NZS 3500.4:2021': 'Plumbing and drainage Part 4 — Heated water services.',
  'AS/NZS 3500.5:2021': 'Plumbing and drainage Part 5 — Housing installations.',
  'AS 1428.1:2021': 'Design for access and mobility — General requirements for access in new building work.',
  'AS 1428.2:1992': 'Design for access and mobility — Enhanced and additional requirements.',
  'AS 1428.4.1:2009': 'Tactile ground surface indicators for orientation of people with vision impairment.',
  'AS 1428.5:2021': 'Communication for people who are deaf or hearing impaired.',
  'AS/NZS 1170.0:2002': 'Structural design actions — General principles.',
  'AS/NZS 1170.1:2002': 'Structural design actions — Permanent, imposed, and other actions.',
  'AS/NZS 1170.2:2021': 'Structural design actions — Wind actions.',
  'AS/NZS 1170.3:2003': 'Structural design actions — Snow and ice actions.',
  'AS 1170.4:2007': 'Structural design actions — Earthquake actions in Australia.',
  'Competition and Consumer Act 2010': 'Australian Consumer Law (ACL) — Product safety, recalls, mandatory standards, and consumer guarantees.',
};

function getStandardDescription(standardName: string): string | null {
  if (STANDARD_DESCRIPTIONS[standardName]) return STANDARD_DESCRIPTIONS[standardName];
  for (const [key, desc] of Object.entries(STANDARD_DESCRIPTIONS)) {
    const baseName = standardName.split(':')[0];
    const keyBase = key.split(':')[0];
    if (baseName === keyBase) return desc;
  }
  return null;
}

/* ── Verification badges ─────────────────────────────────────────
   F4: badges are driven ONLY by the server's per-standard
   verification_status. The old SKILL_VERIFICATION_OVERRIDES map
   hardcoded 14 skills as "verified" client-side, overriding real data —
   a trust claim nothing backed. A standard with no recorded status now
   shows "Unverified"; to earn a Verified pill, backfill
   verification_status in D1 after the verification actually runs.

   The server sends GROUP_CONCAT(DISTINCT verification_status) — a
   comma-joined list of every distinct clause-level value in the standard.
   Live D1 vocabulary (census 2026-07-13): verified, verbatim,
   opus-verified, verified-opus, austlii-verified, verified-<date>,
   phase-2-opus-verified, phase-2-full-census-verified, gold |
   phase-1*, partial, sonnet-verified-opus-pending, phase-2-reingested |
   pending, unverified, phase-0-raw-ingestion, ocr-failed-*, ''.       */

const VERIFIED_STATUSES = new Set([
  'verified', 'verbatim', 'opus-verified', 'verified-opus', 'austlii-verified',
  'phase-2-opus-verified', 'phase-2-full-census-verified', 'gold',
]);
const PARTIAL_STATUSES = new Set([
  'partial', 'sonnet-verified-opus-pending', 'phase-2-reingested',
]);

type VerificationClass = 'verified' | 'warning' | 'partial' | 'unverified';

function classifyStatus(raw: string): VerificationClass {
  // Do NOT drop empty tokens: in "gold," the empty entry means some clauses
  // have no recorded status — that must degrade the badge, not vanish.
  // (The server now coalesces NULL/'' to 'unverified' before GROUP_CONCAT,
  // so empty tokens shouldn't occur; this keeps stale responses honest too.)
  if (!(raw || '').trim()) return 'unverified';
  const parts = (raw || '').toLowerCase().split(',').map(p => p.trim());
  const isVerified = (p: string) => VERIFIED_STATUSES.has(p) || /^verified-\d{4}/.test(p);
  const isPartial = (p: string) => PARTIAL_STATUSES.has(p) || p.startsWith('phase-1');
  if (parts.some(p => p === 'phase-2-opus-verified-amendment-pending')) return 'warning';
  if (parts.every(isVerified)) return 'verified';
  if (parts.some(p => isVerified(p) || isPartial(p))) return 'partial';
  return 'unverified';
}

/* ── Verification badge ──────────────────────────────────────── */

interface VerificationBadgeProps {
  status: string;
}

function VerificationBadge({ status }: VerificationBadgeProps) {
  const cls = classifyStatus(status);
  if (cls === 'verified') {
    return (
      <span
        className="cb-badge cb-badge--verified"
        title="Every clause in this standard has a recorded verification pass"
      >
        Verified
      </span>
    );
  }
  if (cls === 'warning') {
    return (
      <span
        className="cb-badge cb-badge--warning"
        title="Verified via Opus — Amendment 1 review pending. Core clauses verified but recent amendments may not be fully indexed."
      >
        Verified ⚠️
      </span>
    );
  }
  if (cls === 'partial') {
    return (
      <span
        className="cb-badge cb-badge--partial"
        title="Some clauses have a recorded verification pass; others do not"
      >
        Partial
      </span>
    );
  }
  return (
    <span
      className="cb-badge cb-badge--unknown"
      title="No verification has been recorded for this standard"
    >
      Unverified
    </span>
  );
}

/* ── SkillRow — collapsible domain row ───────────────────────── */

interface SkillRowProps {
  skillData: CorpusSkill;
}

/* ── Staleness helpers ────────────────────────────────────────── */

function relativeTimeFromDate(dateStr: string): string {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return '';
  const diffMs = now - then;
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function stalenessColor(dateStr: string): string {
  if (!dateStr) return 'var(--ink-3, #94a3b8)';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return 'var(--ink-3, #94a3b8)';
  const days = Math.floor((now - then) / 86_400_000);
  if (days > 30) return '#ef4444'; // red for stale
  return 'var(--ink-3, #94a3b8)';
}

const stalenessStyle: React.CSSProperties = {
  fontSize: 'var(--t-xs, 11px)',
  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
  marginLeft: '4px',
};

function SkillRow({ skillData }: SkillRowProps) {
  const [expanded, setExpanded] = useState(false);

  const clauseCount = skillData.standards.reduce(
    (acc, s) => acc + (s.clause_count ?? 0),
    0,
  );

  // Derive most recent last_updated across all standards in this skill domain
  const latestUpdate = skillData.standards.reduce((latest, s) => {
    if (!s.last_updated) return latest;
    return !latest || s.last_updated > latest ? s.last_updated : latest;
  }, '' as string);

  // F4: overall skill status derived from real per-standard data only
  const overallStatus = (() => {
    const classes = skillData.standards.map(s => classifyStatus(s.verification_status));
    if (classes.every(c => c === 'verified')) return 'verified';
    if (classes.some(c => c !== 'unverified')) return 'partial';
    return '';
  })();

  return (
    <div className={`cb-skill-card ${expanded ? 'cb-skill-card--expanded' : ''}`}>
      {/* Collapsed / header row */}
      <button
        className="cb-skill-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${skillData.skill}`}
      >
        <span className="cb-skill-name">{SKILL_DISPLAY_NAMES[skillData.skill] || skillData.skill}</span>
        <span className="cb-skill-clauses">
          {clauseCount.toLocaleString()} clause{clauseCount !== 1 ? 's' : ''}
        </span>
        {latestUpdate && (
          <span
            style={{ ...stalenessStyle, color: stalenessColor(latestUpdate) }}
            title={`Last updated: ${latestUpdate}`}
          >
            {relativeTimeFromDate(latestUpdate)}
          </span>
        )}
        <VerificationBadge status={overallStatus} />
        <span className="cb-skill-chevron" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {/* Expanded standards list */}
      {expanded && (
        <ul className="cb-standards-list" role="list">
          {skillData.standards.map((std, idx) => {
            const isLast = idx === skillData.standards.length - 1;
            return (
              <li key={std.standard} className="cb-standard-row" role="listitem">
                <span className="cb-tree-connector" aria-hidden="true">
                  {isLast ? '└─' : '├─'}
                </span>
                <div className="cb-standard-info">
                  <span className="cb-standard-name">{std.standard}</span>
                  {getStandardDescription(std.standard) && (
                    <span className="cb-standard-desc">{getStandardDescription(std.standard)}</span>
                  )}
                </div>
                <span className="cb-standard-clauses">
                  {(std.clause_count ?? 0).toLocaleString()} clause{(std.clause_count ?? 0) !== 1 ? 's' : ''}
                </span>
                {std.last_updated && (
                  <span
                    style={{ ...stalenessStyle, color: stalenessColor(std.last_updated) }}
                    title={`Last updated: ${std.last_updated}`}
                  >
                    {relativeTimeFromDate(std.last_updated)}
                  </span>
                )}
                <VerificationBadge status={std.verification_status} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ── CorpusBrowser — main modal ──────────────────────────────── */

interface CorpusBrowserProps {
  onClose: () => void;
  initialTab?: 'browse' | 'updates';
}

export default function CorpusBrowser({ onClose, initialTab = 'browse' }: CorpusBrowserProps) {
  const [corpus, setCorpus] = useState<CorpusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'browse' | 'updates'>(initialTab);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // R3-M11: Swipe-to-dismiss on mobile
  const swipeStartY = useRef(0);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    swipeStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const dy = e.touches[0].clientY - swipeStartY.current;
    if (dy > 0 && dialogRef.current) {
      dialogRef.current.style.transform = `translateY(${dy}px)`;
      dialogRef.current.style.transition = 'none';
    }
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const dy = e.changedTouches[0].clientY - swipeStartY.current;
    if (dialogRef.current) {
      dialogRef.current.style.transform = '';
      dialogRef.current.style.transition = '';
      if (dy > 100) {
        onClose();
      }
    }
  }, [onClose]);

  /* Fetch corpus on mount */
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchCorpus()
      .then(setCorpus)
      .catch(() => setError('Failed to load corpus data.'))
      .finally(() => setLoading(false));
  }, []);

  /* Close on Escape key */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  /* Trap focus inside modal */
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length) focusable[0].focus();
  }, [loading]);

  /* Close on overlay backdrop click */
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose],
  );

  /* Derived totals */
  const totalClauses = corpus
    ? corpus.skills.reduce(
        (acc, s) => acc + s.standards.reduce((a, std) => a + (std.clause_count ?? 0), 0),
        0,
      )
    : 0;
  const totalDomains = corpus?.skills.length ?? 0;

  /* ── Clipboard export ──────────────────────────────────────── */
  const handleCopyToClipboard = useCallback(() => {
    if (!corpus) return;
    const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const lines: string[] = [
      'Compliance Hub — Corpus Inventory',
      now,
      '',
    ];

    for (const skill of corpus.skills) {
      const skillName = SKILL_DISPLAY_NAMES[skill.skill] || skill.skill;
      const skillClauses = skill.standards.reduce((a, s) => a + (s.clause_count ?? 0), 0);
      // F4: export labels from real data only (no client-side overrides)
      const classes = skill.standards.map(s => classifyStatus(s.verification_status));
      const statusLabel = classes.every(c => c === 'verified')
        ? 'Verified'
        : classes.some(c => c !== 'unverified') ? 'Partial' : 'Unverified';

      lines.push(`${skillName}  —  ${skillClauses.toLocaleString()} clauses  —  ${statusLabel}`);

      skill.standards.forEach((std, idx) => {
        const isLast = idx === skill.standards.length - 1;
        const connector = isLast ? '└─' : '├─';
        const desc = getStandardDescription(std.standard);
        const stdCls = classifyStatus(std.verification_status);
        const stdStatus = stdCls === 'verified' || stdCls === 'warning'
          ? 'Verified'
          : stdCls === 'partial' ? 'Partial' : 'Unverified';
        lines.push(`  ${connector} ${std.standard}  —  ${(std.clause_count ?? 0).toLocaleString()} clauses  —  ${stdStatus}`);
        if (desc) {
          const pad = isLast ? '     ' : '  │  ';
          lines.push(`${pad} ${desc}`);
        }
      });
      lines.push('');
    }

    lines.push(`Total: ${totalClauses.toLocaleString()} clauses across ${totalDomains} domains`);

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2500);
    }).catch(() => {
      // Fallback: select + copy
      const ta = document.createElement('textarea');
      ta.value = lines.join('\n');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2500);
    });
  }, [corpus, totalClauses, totalDomains]);

  return (
    <div
      className="cb-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="presentation"
      aria-hidden="false"
    >
      <div
        className="cb-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Corpus Browser"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="cb-header">
          <h2 className="cb-title">Corpus Browser</h2>
          <button
            className="cb-close"
            onClick={onClose}
            aria-label="Close corpus browser"
          >
            ✕
          </button>
        </div>

        {/* Summary bar */}
        {corpus && !loading && (
          <div className="cb-summary-bar">
            <span className="cb-summary-stat">
              {totalClauses.toLocaleString()} clauses
            </span>
            <span className="cb-summary-sep">across</span>
            <span className="cb-summary-stat">{totalDomains}</span>
            <span className="cb-summary-sep">
              domain{totalDomains !== 1 ? 's' : ''}
            </span>
            <button
              className="cb-copy-btn"
              onClick={handleCopyToClipboard}
              title="Copy full corpus list to clipboard"
              aria-label="Copy corpus list to clipboard"
            >
              {copyStatus === 'copied' ? '✓ Copied' : '📋 Copy list'}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="cb-tabs">
          <button className={`cb-tab ${activeTab === 'browse' ? 'cb-tab--active' : ''}`}
            onClick={() => setActiveTab('browse')}>Browse</button>
          <button className={`cb-tab ${activeTab === 'updates' ? 'cb-tab--active' : ''}`}
            onClick={() => {
              setActiveTab('updates');
              try { localStorage.setItem('ch-corpus-updates-seen', new Date().toISOString()); } catch { /* noop */ }
            }}>Updates</button>
        </div>

        {/* Body */}
        <div className="cb-body">
          {/* Updates feed tab */}
          {activeTab === 'updates' && (
            <div className="cb-updates-feed">
              {UPDATES.length === 0 ? (
                <div className="cb-updates-empty" style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>📡</div>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>No recent updates</div>
                  <div style={{ fontSize: '12px' }}>Corpus change notifications will appear here as standards are added or verified.</div>
                </div>
              ) : (
                UPDATES.map((update, i) => (
                  <div key={i} className="cb-update-item">
                    <div className="cb-update-dot" />
                    <div className="cb-update-content">
                      <span className="cb-update-title">{update.title}</span>
                      <span className="cb-update-desc">{update.description}</span>
                      <span className="cb-update-time">{update.time}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Browse tab content */}
          {activeTab === 'browse' && (
            <>
              {loading && (
                <div className="cb-loading" role="status" aria-label="Loading corpus">
                  <span className="corpus-loading-dot" />
                  <span className="corpus-loading-dot" />
                  <span className="corpus-loading-dot" />
                </div>
              )}

              {error && !loading && (
                <div className="cb-error" role="alert">
                  <span>Error: {error}</span>
                  <button
                    className="cb-retry"
                    onClick={() => {
                      setError(null);
                      setLoading(true);
                      fetchCorpus()
                        .then(setCorpus)
                        .catch(() => setError('Failed to load corpus data.'))
                        .finally(() => setLoading(false));
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && corpus && corpus.skills.length === 0 && (
                <p className="cb-empty">No corpus data available.</p>
              )}

              {!loading && !error && corpus && corpus.skills.length > 0 && (
                <div className="cb-skills-list" role="list">
                  {corpus.skills.map((skill) => (
                    <SkillRow key={skill.skill} skillData={skill} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
